<template>
  <section class="ui-card fin-hero">
    <div class="fin-hero__main">
      <template v-if="balance !== null">
        <div class="fin-hero__label" :class="positive ? 'is-in' : 'is-out'">
          {{ positive ? $t("finance_hero_you_receive") : $t("finance_hero_you_owe") }}
        </div>
        <div class="fin-hero__value fin-num" :class="positive ? 'is-in' : 'is-out'">
          {{ amountText }}<small>{{ currency }}</small>
        </div>
        <div class="fin-hero__sub">
          <span>{{ hint }}</span>
          <button type="button" class="ui-link" @click="positive ? $emit('navigate', 'settlements') : $emit('how-to-pay')">
            {{ positive ? $t("finance_payout_history") : $t("finance_how_to_pay") }}
          </button>
        </div>
        <div v-if="conflict" class="ui-banner ui-banner--info ui-banner--warn">
          <v-icon size="16" color="currentColor">mdi-alert-outline</v-icon>
          <span>
            {{ $t("finance_schedule_conflict") }}
            <button type="button" class="ui-link" @click="$emit('navigate', 'contract')">{{ $t("finance_shell.open_contract") }}</button>
          </span>
        </div>
      </template>
      <div v-else class="fin-hero__sub">{{ $t("finance_balance_unavailable") }}</div>
    </div>

    <div class="fin-hero__split">
      <div class="fin-eyebrow">{{ $t("finance_shell.hero.of_turnover", { amount: money(turnover) }) }}</div>
      <template v-if="empty">
        <p class="ui-muted">{{ $t("finance_shell.hero.no_turnover") }}</p>
      </template>
      <template v-else>
        <div class="fin-split" role="img" :aria-label="splitLabel">
          <i v-if="share !== null" class="fin-split__keep" :style="{ width: `${share}%` }"></i>
          <i class="fin-split__cut" :style="{ width: `${share === null ? 100 : 100 - share}%` }"></i>
        </div>
        <div class="fin-legend">
          <span v-if="share !== null" class="fin-legend__keep">
            {{ $t("finance_shell.hero.you_keep") }} <b class="fin-num">{{ money(net) }}</b> ({{ share }} %)
          </span>
          <span class="fin-legend__cut">
            {{ share === null ? $t("finance_shell.hero.deductions_exceed") : $t("finance_shell.hero.deductions") }}
            <b class="fin-num">{{ money(deductions) }}</b>
          </span>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { PAYOUT_SCHEDULE_LABEL_KEYS, formatMoney } from "@/components/restaurant/finance/constants";
import { shareOfTurnover } from "../financeLogic";

/**
 * Veliki broj Zarade: "Sledi vam za isplatu" (zeleno) ili "Dugujete Orderi" (amber, nikad crveno), sljedeći obračun
 * i traka koja pokazuje koliko od prometa ostaje restoranu. Ne zna ništa o učitavanju: dobija već izračunate brojke.
 */
export default {
  name: "FinanceHero",
  props: {
    balance: { type: Number, default: null },
    currency: { type: String, default: "KM" },
    /** { schedule, thresholdAmount, nextDate } iz resolveSettlement */
    settlement: { type: Object, default: () => ({}) },
    conflict: { type: Boolean, default: false },
    turnover: { type: Number, default: 0 },
    net: { type: Number, default: 0 },
    deductions: { type: Number, default: 0 },
    empty: { type: Boolean, default: false },
  },
  computed: {
    positive() {
      return this.balance >= 0;
    },
    amountText() {
      return formatMoney(Math.abs(this.balance || 0), "");
    },
    share() {
      return shareOfTurnover(this.net, this.turnover);
    },
    scheduleLabel() {
      const { schedule, thresholdAmount } = this.settlement;
      if (!schedule) {
        return this.$t("finance_schedule_not_agreed");
      }
      if (schedule === "THRESHOLD" && thresholdAmount !== null && thresholdAmount !== undefined) {
        return this.$t("finance_schedule_threshold_amount", { amount: this.money(thresholdAmount) });
      }
      return this.$t(PAYOUT_SCHEDULE_LABEL_KEYS[schedule] || "finance_schedule_not_agreed");
    },
    hint() {
      const { schedule, nextDate } = this.settlement;
      if (!schedule) {
        return this.$t("finance_schedule_not_agreed");
      }
      return nextDate
        ? this.$t("finance_current_balance_next", { date: this.localeDate(nextDate), schedule: this.scheduleLabel })
        : this.$t("finance_current_balance_schedule", { schedule: this.scheduleLabel });
    },
    splitLabel() {
      return this.share === null
        ? this.$t("finance_shell.hero.deductions_exceed")
        : this.$t("finance_shell.hero.split_label", { turnover: this.money(this.turnover), net: this.money(this.net) });
    },
  },
  methods: {
    money(value) {
      return formatMoney(value, this.currency);
    },
  },
};
</script>
